import React, { PropsWithChildren } from 'react'
import classNames from 'classnames'
import { Box } from '@alto-avios/alto-ui'
import { deviceSizes } from '~/styles/deviceSizes'
import { ContentfulImage } from '../ContentfulImage/ContentfulImage'
import { CardProps, CardImageProps } from './Card.types'
import styles from './Card.module.scss'

function CardImage({
  src,
  alt,
  fill = false,
  width = 400,
  height = 300,
  format,
  contentType,
  badge,
}: Readonly<CardImageProps>) {
  return (
    <Box
      className={classNames(styles.imageContainer, { [styles.fillImage]: fill })}
      position={fill ? 'absolute' : 'relative'}
      display="block"
    >
      <ContentfulImage
        src={src}
        sizes={[
          { width, height, media: `(min-width: ${deviceSizes.md})`, densities: [1, 2] },
          { width, height, media: `(max-width: ${deviceSizes.md})`, densities: [1, 2] },
        ]}
        alt={alt}
        format={format}
        contentType={contentType}
        className={styles.imageWrapper}
        imageClassName={styles.image}
      />
      {badge && (
        <Box className={styles.imageBadge} position="absolute">
          {badge}
        </Box>
      )}
    </Box>
  )
}

function CardContent({
  children,
  overlay = false,
}: Readonly<PropsWithChildren<{ overlay?: boolean }>>) {
  return (
    <Box
      className={classNames(styles.content, { [styles.overlayContent]: overlay })}
      display="flex"
      flexDirection="column"
      padding="md"
      gap="sm"
      flexGrow={1}
      flexShrink={0}
      flexBasis="auto"
    >
      {children}
    </Box>
  )
}

function CardButtonWrapper({ children }: Readonly<PropsWithChildren>) {
  return <Box className={styles.cardButtonWrapper}>{children}</Box>
}

function Card({
  children,
  backgroundColor = 'base',
  layout = 'default',
}: PropsWithChildren<CardProps>) {
  return (
    <Box
      className={classNames(styles.card, {
        [styles.border]: backgroundColor === 'base',
        [styles.imageBackground]: layout === 'imageBackground',
      })}
      display="flex"
      flexDirection="column"
      borderRadius="md"
      backgroundColor={backgroundColor}
    >
      {children}
    </Box>
  )
}

Card.Image = CardImage
Card.Content = CardContent
Card.ButtonWrapper = CardButtonWrapper

export default Card
